import { ITrack } from '@/data/models/track';
import { loadAlbum } from '@/state/actions/albumAction';
import store from '@/state/store';
import _, {
  cloneDeep,
  every,
  isEmpty,
  isEqual,
  isNil,
  map,
  mapValues,
  pickBy,
  some,
  zip,
} from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { IArtist } from '@/data/models/artist';
import { IMixedField, ITrackMixed } from '@/data/models/trackMixed';
import {
  BASE_TRACK_TEMPLATE,
  COMPLEX_ARRAY_FIELD_CONFIG,
  COMPLEX_ARRAY_FIELDS,
  FIELDS_TO_EVALUATE,
} from '@/constants/MultiTracksConstants';
import { IAlbum } from '@/data/models/album';
import { productApiClient } from '@/clients/ProductAPIClient';
import { ComplexArrayFieldName } from '@/types/multiple-track-types';
import { IChangeRequest } from '@/data/models/changeRequest';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';
import { ResponseModel } from '@/data/models/model';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';
import { logger } from '@/utils/Logger';

interface UseMultipleTrackEditViewProps {
  togglePopUp: () => void;
  albumObj: IAlbum;
  trackObj: ITrack;
  reloadTracks: () => void;
  multipleTracks: ITrack[];
  onSaveSuccess?: () => void;
  changeRequest?: IChangeRequest;
}

const getEmptyFieldMixedValues = (fieldName: ComplexArrayFieldName) => {
  const config = COMPLEX_ARRAY_FIELD_CONFIG[fieldName];
  if (!config) return null;

  const mixedField: Record<string, any> = { ...config.defaults };
  config.keys.forEach((key: any) => {
    mixedField[key] = 'Mixed';
  });

  return mixedField;
};

export const useMultipleTrackEditView = ({
  togglePopUp,
  albumObj,
  trackObj,
  reloadTracks,
  multipleTracks,
  onSaveSuccess,
  changeRequest,
}: UseMultipleTrackEditViewProps) => {
  const initialTrack = cloneDeep(trackObj);
  const [track, setTrack] = useState<ITrack>(initialTrack);
  const [showPopUp, setShowPopUp] = useState(true);
  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setTrack(cloneDeep(trackObj));
  }, [trackObj]);

  const getMixedField = (value?: any, isMixed = true, isReadOnly = false) => {
    const normalizedValue =
      value === undefined ? (isMixed ? 'Mixed' : null) : value;

    return {
      value: normalizedValue,
      originalValue: cloneDeep(normalizedValue),
      isMixed,
      isReadOnly,
    };
  };

  /**
   * Evaluates an array of fields to determine if their target properties are identical or mixed.
   * If discrepancies are found for a specific property across the array items, that property is flagged as 'Mixed'.
   * @param {any[]} fieldValues
   * @param {ComplexArrayFieldName} fieldName
   * @param {number} fieldIndex
   */
  const setFieldArrayMixedValues = (
    fieldValues: any[],
    fieldName: ComplexArrayFieldName,
    fieldIndex: number
  ) => {
    const config = COMPLEX_ARRAY_FIELD_CONFIG[fieldName];
    if (!config || some(fieldValues, isNil)) {
      return getMixedField(
        getEmptyFieldMixedValues(fieldName),
        true,
        fieldIndex !== 0
      );
    }

    const firstItem = fieldValues[0];
    const fieldArrayMixedValues: Record<string, any> = { ...config.defaults };
    config.keys.forEach((key: any) => {
      // _.every check if ALL items are equal to the first item's key.
      const areAllEqual = every(fieldValues, (item) =>
        isEqual(item[key], firstItem[key])
      );
      fieldArrayMixedValues[key] = areAllEqual ? firstItem[key] : 'Mixed';
    });

    return getMixedField(fieldArrayMixedValues, true);
  };

  /**
   * Evaluates and groups complex array fields across multiple tracks, determining if their values
   * are identical or mixed at each specific index.
   * @param {keyof ITrackMixed} field - The specific track field to evaluate (e.g., 'artists').
   * @param {boolean} isMixed - Flag indicating if the parent level is already considered mixed.
   */
  const setComplexArrayMixedValues = (
    field: keyof ITrackMixed,
    isMixed: boolean
  ) => {
    const baseTrack = getMixedField([], isMixed);
    const baseTrackValues = baseTrack.value as IMixedField[];
    const extractedFieldValue: any = map(multipleTracks, (track) => {
      return !isNil(track[field]) ? track[field] : [];
    });
    const fieldValuesGroupByIndex = zip(...extractedFieldValue);

    fieldValuesGroupByIndex.forEach((fieldValues, index) => {
      const valuesAreEqual = every(fieldValues, (value) => {
        return value !== undefined && isEqual(value, fieldValues[0]);
      });
      if (valuesAreEqual) {
        baseTrackValues.push(getMixedField(cloneDeep(fieldValues[0])!, false));
      } else {
        const isComplexArrayField = COMPLEX_ARRAY_FIELDS.includes(field);
        if (isComplexArrayField) {
          baseTrackValues.push(
            setFieldArrayMixedValues(
              fieldValues,
              field as ComplexArrayFieldName,
              index
            )
          );
        } else {
          baseTrackValues.push(getMixedField());
        }
      }
    });

    baseTrack.originalValue = cloneDeep(baseTrack.value);
    return baseTrack;
  };

  /**
   * Evaluates all tracks to generate a unified base track.
   * Flags differing flat fields as 'Mixed' and delegates complex arrays for deep comparison.
   * @returns {ITrackMixed | null} The evaluated base track, or null if tracks are missing.
   */
  const getBaseMixedTrack = (): ITrackMixed | null => {
    if (!multipleTracks || multipleTracks.length === 0) return null;

    const baseTrack = cloneDeep(BASE_TRACK_TEMPLATE);

    FIELDS_TO_EVALUATE.forEach((field: keyof ITrackMixed) => {
      const referenceField = multipleTracks[0][field];
      const hasFieldDifferences = some(multipleTracks, (track) => {
        return !isEqual(track[field], referenceField);
      });

      if (COMPLEX_ARRAY_FIELDS.includes(field)) {
        baseTrack[field] = setComplexArrayMixedValues(
          field,
          hasFieldDifferences
        );
      } else {
        baseTrack[field] = hasFieldDifferences
          ? getMixedField()
          : getMixedField(referenceField, false);
      }
    });

    const artistsField = baseTrack.artists as any;
    if (artistsField?.isMixed) {
      baseTrack.displayArtist = getMixedField();
    }

    return baseTrack;
  };

  const [baseMixedTrack, setBaseMixedTrack] = useState<ITrackMixed>(() => {
    if (multipleTracks?.length) {
      return getBaseMixedTrack() || BASE_TRACK_TEMPLATE;
    }
    return BASE_TRACK_TEMPLATE;
  });
  const [initialMixedTrack] = useState<ITrackMixed>(() =>
    cloneDeep(baseMixedTrack)
  );

  const handleInput = (field: keyof ITrackMixed, value: any) => {
    const updatedField = baseMixedTrack[field] as IMixedField;
    updatedField.value = value;
    setBaseMixedTrack({ ...baseMixedTrack, [field]: updatedField });
  };

  const handleArtist = (artistData: IMixedField[]) => {
    handleInput('artists', artistData);
  };

  const handleDisplayArtist = (artistData: IArtist[]): string => {
    if (_.isEmpty(artistData)) return '';

    const mainArtists = _.join(
      _.map(_.filter(artistData, { performerRole: 'Main' }), 'name'),
      ', '
    );
    const featuredArtists = _.join(
      _.map(_.filter(artistData, { performerRole: 'Featured' }), 'name'),
      ', '
    );
    const featuredText = featuredArtists ? ` feat. ${featuredArtists}` : '';

    return `${mainArtists}${featuredText}`;
  };

  const handleWriterComposer = (writerComposerData: IMixedField[]) => {
    handleInput('writersComposers', writerComposerData);
  };

  const handlePublisher = (publisherData: IMixedField[]) => {
    handleInput('publishers', publisherData);
  };

  /**
   * Merges updated complex array values (artists, writers, publishers) into the current track.
   * Handles 'Mixed' fallback logic and filters out empty entities.
   */
  const mergeComplexEntityValues = (
    currentTrackArray: any[],
    extractedField: IMixedField,
    fieldName: ComplexArrayFieldName
  ) => {
    const extractedValues = extractedField?.value;
    if (!Array.isArray(extractedValues) || extractedValues.length === 0)
      return [];

    const currentArray = Array.isArray(currentTrackArray)
      ? currentTrackArray
      : [];
    const config = COMPLEX_ARRAY_FIELD_CONFIG[fieldName];

    const secondaryKey = config.keys.find((k: any) => k !== 'name') as string;

    const mappedEntities = extractedValues.map((changedEntity, index) => {
      const newValue = changedEntity?.value || {};
      const originalValue = changedEntity?.originalValue || {};

      let matchedEntity = currentArray.find(
        (item) =>
          item.name === originalValue.name &&
          item[secondaryKey] === originalValue[secondaryKey]
      );

      if (
        !matchedEntity &&
        (originalValue.name === 'Mixed' ||
          originalValue[secondaryKey] === 'Mixed')
      ) {
        matchedEntity = currentArray[index];
      }

      if (!matchedEntity) {
        return {
          ...newValue,
          name: newValue.name === 'Mixed' ? '' : newValue.name ?? '',
          [secondaryKey]:
            newValue[secondaryKey] === 'Mixed'
              ? ''
              : newValue[secondaryKey] ?? 'Main',
          sequence: newValue.sequence ?? 0,
        };
      }

      const mergedEntity = { ...matchedEntity };
      config.keys.forEach((key: any) => {
        if (newValue[key] !== undefined) {
          mergedEntity[key] =
            newValue[key] === 'Mixed' && changedEntity.isMixed
              ? matchedEntity[key]
              : newValue[key] ?? '';
        }
      });

      if (newValue.sequence !== undefined) {
        mergedEntity.sequence =
          matchedEntity.sequence ?? newValue.sequence ?? 0;
      }

      return mergedEntity;
    });

    return mappedEntities.filter((entity) => {
      const secValue = entity[secondaryKey];
      const isSecondaryEmpty =
        !secValue || secValue === 'Please Select' || secValue === '';
      return !(entity.name === '' && isSecondaryEmpty);
    });
  };

  /**
   * Processes and submits changes made to multiple tracks simultaneously.
   *
   * This method verifies if there are any changes by comparing the current state
   * with the original (`baseMixedTrack`). If modifications exist, it iterates through
   * the selected tracks (`multipleTracks`), applies the new values (handling complex
   * fields via `mergeComplexEntityValues`), updates the display artist, and performs
   * concurrent API requests to save the data. Finally, it manages the UI states
   * (loaders, data reloading, and closing modals).
   *
   * @async
   * @function handleSubmit
   * @param {boolean} closeModal - Indicates whether to close the modal/pop-up after a successful update or if there are no changes to save.
   * @throws Logs an error to the console if the API update request for the tracks fails.
   */
  const handleSubmit = async (closeModal: boolean) => {
    setIsSubmitting(true);
    setSaveErrorMessage(null);
    if (isNil(multipleTracks)) {
      setIsSubmitting(false);
      return;
    }

    const changedTrackFields = pickBy(
      baseMixedTrack,
      (mt) => !isEqual(mt?.value, mt?.originalValue)
    );
    if (isEmpty(changedTrackFields)) {
      setShowPopUp(false);
      togglePopUp();
      onSaveSuccess?.();
      setIsSubmitting(false);
      return;
    }

    const updateTracksList = multipleTracks.reduce(
      (acc, currentTrack: ITrack) => {
        const updatedTrack = mapValues(currentTrack, (value, field) => {
          const extractedValue = _.get(
            changedTrackFields,
            field
          ) as IMixedField;

          if (extractedValue === undefined) return value;
          if (COMPLEX_ARRAY_FIELDS.includes(field as keyof ITrackMixed)) {
            return mergeComplexEntityValues(
              value as any[],
              extractedValue,
              field as ComplexArrayFieldName
            );
          }
          return extractedValue.value;
        });

        if (updatedTrack.artists) {
          updatedTrack.displayArtist = handleDisplayArtist(
            updatedTrack.artists as IArtist[]
          );
        }

        acc[updatedTrack.id as string] = updatedTrack!;
        return acc;
      },
      {} as Record<string, any>
    );

    const updatedAlbum = {
      ...albumObj,
      tracks: albumObj.tracks.map((track: ITrack) => {
        const modifiedData = updateTracksList[track.id!];

        if (modifiedData) {
          return {
            ...track,
            ...modifiedData,
          };
        }

        return track;
      }),
    };

    try {
      let response: ResponseModel<IAlbum> | ResponseModel<IChangeRequest>;
      if (changeRequest) {
        response = await changeRequestApiClient.updateChangeRequest(
          changeRequest.albumId,
          changeRequest.id!,
          { ...changeRequest, changeRequestAlbum: updatedAlbum }
        );
      } else {
        response = await productApiClient.updateProduct(
          updatedAlbum.id!,
          updatedAlbum
        );
      }

      if (!isEmpty(response)) {
        await store.dispatch(loadAlbum({ isJustCreated: false }));
        reloadTracks();
        if (closeModal) {
          onSaveSuccess?.();
        }
      }

      if (closeModal) {
        setShowPopUp(false);
        togglePopUp();
      }
    } catch (error: any) {
      logger.error(`Failed to update multiple tracks ${error}`);
      setSaveErrorMessage(getSaveErrorMessage(error, !!changeRequest));
    } finally {
      setIsSubmitting(false);
    }
  };

  const closePopUp = () => {
    setShowPopUp(false);
    togglePopUp();
  };

  const onCancel = useCallback(() => {
    if (_.isEqual(baseMixedTrack, initialMixedTrack)) {
      closePopUp();
    } else {
      setShowCancelChangesModal(true);
    }
  }, [baseMixedTrack, initialMixedTrack]);

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onCancel]);

  return {
    track,
    showPopUp,
    onCancel,
    closePopUp,
    handleInput,
    isSubmitting,
    saveErrorMessage,
    setSaveErrorMessage,
    handleSubmit,
    handleArtist,
    handleWriterComposer,
    handlePublisher,
    showCancelChangesModal,
    setShowCancelChangesModal,
    baseMixedTrack,
    setBaseMixedTrack,
  };
};
